import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import { useState, useEffect, useCallback } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { EnvironmentInfo, ToggleEnvironmentButton } from '../common';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  systemSection: {
    marginBottom: theme.spacing(3),
  },
  systemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  systemName: {
    fontWeight: 600,
  },
  envCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  stickyDetail: {
    position: 'sticky',
    top: '1rem',
  },
}));

interface SystemWithEnvironments {
  system: Entity;
  environments: Entity[];
}

const byName = (a: Entity, b: Entity) => a.metadata.name.localeCompare(b.metadata.name);

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

interface SystemGroupProps {
  group: SystemWithEnvironments;
  selectedUid: string | undefined;
  onSelect: (env: Entity) => void;
  showDivider: boolean;
}

const SystemGroup = ({ group, selectedUid, onSelect, showDivider }: SystemGroupProps) => {
  const classes = useStyles();
  const { system, environments } = group;

  return (
    <Box className={classes.systemSection}>
      <Box
        className={classes.systemHeader}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box style={{ width: '100%' }}>
          <Typography variant="subtitle1" className={classes.systemName}>
            {system.metadata.title || system.metadata.name}
          </Typography>
          <Typography className={classes.envCount}>
            ({plural(environments.length, 'environment')})
          </Typography>
          <Alert severity="info" style={{ width: '100%' }}>
            For detailed information on the workflow run, access the project actions here:
            <br />
            <Link
              href={`https://github.com/${system.metadata.annotations?.['github.com/project-slug']}/actions`}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
            >
              {system.metadata.annotations?.['github.com/project-slug']} workflow actions
            </Link>
          </Alert>
        </Box>
      </Box>

      {environments.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ paddingLeft: 4 }}>
          No environments found for this system
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {environments.map(env => (
            <Grid item xs={12} key={env.metadata.uid}>
              <ToggleEnvironmentButton
                entity={env}
                isSelected={selectedUid === env.metadata.uid}
                onSelect={() => onSelect(env)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {showDivider && (
        <Box mt={3}>
          <Divider />
        </Box>
      )}
    </Box>
  );
};

export const DashboardEnvironmentsModule = () => {
  const classes = useStyles();
  const { entity: domainEntity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const [systemGroups, setSystemGroups] = useState<SystemWithEnvironments[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<Entity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSystemEnvironments = useCallback(
    async (system: Entity): Promise<SystemWithEnvironments> => {
      const { items: environments } = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'environment',
          'spec.system': system.metadata.name,
        },
      });
      return { system, environments: environments.slice().sort(byName) };
    },
    [catalogApi],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const { items: systems } = await catalogApi.getEntities({
          filter: {
            kind: 'System',
            'spec.domain': domainEntity.metadata.name,
          },
        });

        const sortedSystems = systems.slice().sort(byName);
        const groups = await Promise.all(sortedSystems.map(fetchSystemEnvironments));

        setSystemGroups(groups);
        setError(null);

        // Auto-select first environment of the first system that has one
        const firstEnv = groups.find(g => g.environments.length > 0)?.environments[0];
        if (firstEnv) {
          setSelectedEnv(firstEnv);
        }
      } catch {
        setError('Failed to fetch environments');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [catalogApi, domainEntity.metadata.name, fetchSystemEnvironments]);

  const totalEnvCount = systemGroups.reduce((sum, g) => sum + g.environments.length, 0);

  const summaryText = loading
    ? 'Loading...'
    : `${plural(systemGroups.length, 'system')}, ${plural(totalEnvCount, 'environment')} found`;

  const renderEnvList = () => {
    if (loading) {
      return (
        <Paper style={{ padding: '2rem', textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginTop: '1rem' }}>
            Loading environments...
          </Typography>
        </Paper>
      );
    }

    if (error) {
      return (
        <Paper style={{ padding: '2rem', textAlign: 'center' }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      );
    }

    if (systemGroups.length === 0) {
      return (
        <Paper style={{ padding: '2rem', textAlign: 'center' }}>
          <Typography variant="body1">No systems found for this domain</Typography>
        </Paper>
      );
    }

    return (
      <>
        {systemGroups.map((group, idx) => (
          <SystemGroup
            key={group.system.metadata.uid}
            group={group}
            selectedUid={selectedEnv?.metadata.uid}
            onSelect={setSelectedEnv}
            showDivider={idx < systemGroups.length - 1}
          />
        ))}
      </>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box mb={2}>
          <Typography variant="h6">Environment List</Typography>
          <Typography variant="body2" color="textSecondary">
            {summaryText}
          </Typography>
        </Box>
        {renderEnvList()}
      </Grid>
      <Grid item xs={12} md={6}>
        <Box className={classes.stickyDetail}>
          <Box mb={2}>
            <Typography variant="h6">Environment Details</Typography>
          </Box>
          <EnvironmentInfo entity={selectedEnv} />
        </Box>
      </Grid>
    </Grid>
  );
};
